import { useCallback, useMemo } from "react";
import { useCommsNewEndpoints } from "@certa/common/toggles";
import {
  createOptimisticMessage,
  handleOptimisticMessageReplacement,
  type OptimisticConversationMessagesModel,
  type OptimisticId
} from "../util/optimisticMessage.utils";
import type { AddCommentResponse } from "@certa/queries/types/comments.types";
import type { ConversationMessagesModel } from "@certa/queries/queries/comms";
import { useAuth } from "@certa/auth/AuthenticationProvider";

export const useOptimisticMessageHandling = (
  activeConversationThreadCommsId: string | undefined,
  addOptimisticMessage: (message: OptimisticConversationMessagesModel) => void,
  removeOptimisticMessage: (messageId: OptimisticId) => void,
  replaceOptimisticMessage: (
    optimisticId: OptimisticId,
    actualMessage: ConversationMessagesModel
  ) => void
) => {
  const { user } = useAuth();
  const currentUser = useMemo(() => user || { id: 0 }, [user]);
  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  const handleOptimisticAdd = useCallback(
    ({
      messageContent,
      mentionedUsers,
      mentionedGroups,
      hasAttachment
    }: {
      messageContent: string;
      mentionedUsers: number[];
      mentionedGroups: number[];
      hasAttachment: boolean;
    }) => {
      if (
        isCommsNewEndpointEnabled &&
        !hasAttachment &&
        activeConversationThreadCommsId
      ) {
        const optimisticMessage = createOptimisticMessage({
          messageContent,
          conversationId: activeConversationThreadCommsId,
          mentionedUsers,
          mentionedGroups,
          currentUser
        });

        addOptimisticMessage(optimisticMessage);
        return optimisticMessage.kryptonId;
      }

      return null;
    },
    [
      isCommsNewEndpointEnabled,
      addOptimisticMessage,
      activeConversationThreadCommsId,
      currentUser
    ]
  );

  const handleOptimisticReplace = useCallback(
    (optimisticMessageId: string, serverResponse: AddCommentResponse) => {
      if (activeConversationThreadCommsId) {
        handleOptimisticMessageReplacement({
          optimisticMessageId,
          serverResponse,
          conversationId: activeConversationThreadCommsId,
          currentUser,
          replaceOptimisticMessage,
          removeOptimisticMessage
        });
      }
    },
    [
      activeConversationThreadCommsId,
      currentUser,
      replaceOptimisticMessage,
      removeOptimisticMessage
    ]
  );

  const handleOptimisticRemove = useCallback(
    (optimisticMessageId: string) => {
      removeOptimisticMessage(optimisticMessageId);
    },
    [removeOptimisticMessage]
  );

  return {
    handleOptimisticAdd,
    handleOptimisticReplace,
    handleOptimisticRemove,
    isOptimisticEnabled:
      isCommsNewEndpointEnabled && !!activeConversationThreadCommsId
  };
};
